// Matches CommonMark's actual fence-closing rule (a closing fence needs the
// same character, a run at least as long as the opener's, and no info
// string) — `{3,}`/`~{3,}` capture the FULL run length, not just 3
// characters, so a 4+-backtick fence isn't mistaken for closed by a shorter
// nested example inside it. The trailing group is `\S*` (not `\w*`): any
// non-whitespace content — not just word characters — after the fence marker
// is an info string per CommonMark, and must disqualify the line as a
// closer. `\w*` let a line like "```---" match with an empty captured info
// string (the "---" simply went uncaptured, since the pattern isn't
// end-anchored), wrongly treating it as a valid close.
export const FENCE_LINE_PATTERN = /^(\s{0,3})(`{3,}|~{3,})(\s*)(\S*)/

export type OpenFence = { lineIndex: number; marker: string }
export type FenceLine = { content: string; separator: string }

export function getFenceMatch(line: string) {
  return FENCE_LINE_PATTERN.exec(line)
}

export function canOpenFence(line: string, match: RegExpExecArray): boolean {
  const [, indent, marker] = match
  const infoString = line.slice(indent.length + marker.length)
  return /^ {0,3}$/.test(indent) && (marker[0] === "~" || !infoString.includes("`"))
}

export function closesFence(open: OpenFence, line: string, match: RegExpExecArray): boolean {
  const [, indent, marker] = match
  const trailingText = line.slice(indent.length + marker.length)
  return (
    /^ {0,3}$/.test(indent) &&
    marker[0] === open.marker[0] &&
    marker.length >= open.marker.length &&
    /^[ \t]*$/.test(trailingText)
  )
}

export function splitFenceLines(markdown: string): FenceLine[] {
  const parts = markdown.split(/(\r\n|\r|\n)/)
  const lines: FenceLine[] = []
  for (let index = 0; index < parts.length; index += 2) {
    lines.push({ content: parts[index], separator: parts[index + 1] ?? "" })
  }
  return lines
}

/**
 * Finds the fence (if any) still open at the end of `lines`. Single source
 * of truth for fence state across the chat markdown pipeline and the
 * artifact stream parser — independent reimplementations previously
 * truncated fence runs to exactly 3 characters and/or ignored info strings,
 * which misfired inside a fence containing a shorter nested example with
 * its own fence-looking lines.
 */
export function findOpenFence(lines: string[]): OpenFence | null {
  let open: OpenFence | null = null

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const match = getFenceMatch(lines[lineIndex])
    if (!match) continue

    const marker = match[2]

    if (open === null) {
      if (canOpenFence(lines[lineIndex], match)) open = { lineIndex, marker }
    } else if (closesFence(open, lines[lineIndex], match)) {
      open = null
    }
  }

  return open
}

/**
 * Masks only unambiguous top-level fenced code for delimiter detection.
 * Container-prefixed, indented, and malformed candidates deliberately remain
 * visible to the caller (fail open) rather than growing a Markdown parser here.
 * The result preserves every UTF-16 index and line ending so indexes found in
 * it can safely slice the original Markdown.
 */
export function maskFencedCodeForDelimiterDetection(markdown: string): string {
  const lines = splitFenceLines(markdown)
  let open: OpenFence | null = null

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex].content
    const match = getFenceMatch(line)

    if (open) {
      lines[lineIndex].content = " ".repeat(line.length)
      if (match) {
        if (closesFence(open, line, match)) open = null
      }
      continue
    }

    if (!match) continue
    if (!canOpenFence(line, match)) continue

    open = { lineIndex, marker: match[2] }
    lines[lineIndex].content = " ".repeat(line.length)
  }

  return lines.map(({ content, separator }) => content + separator).join("")
}
